import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock3, Copy, FileCheck, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { Card, Badge, Button, Spinner } from '../../components/ui/Primitives';
import { formatDate, formatNGN, truncateAddress } from '../../utils/helpers';

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { getOrder, fetchOrderById, loading, cancelOrder } = useOrders();
  const [fallbackOrder, setFallbackOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailResolved, setDetailResolved] = useState(false);

  const order = getOrder(orderId) || fallbackOrder;

  useEffect(() => {
    let active = true;
    const existingOrder = getOrder(orderId);

    setFallbackOrder(existingOrder || null);
    setDetailResolved(Boolean(existingOrder));

    if (loading || existingOrder || !orderId) {
      return () => {
        active = false;
      };
    }

    setDetailLoading(true);
    fetchOrderById(orderId)
      .then((fetchedOrder) => {
        if (!active) return;
        setFallbackOrder(fetchedOrder || null);
        setDetailResolved(true);
      })
      .catch(() => {
        if (!active) return;
        setFallbackOrder(null);
        setDetailResolved(true);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [orderId, getOrder, fetchOrderById, loading]);

  if (loading || detailLoading) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem', background: 'var(--clr-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={28} />
      </div>
    );
  }

  if (!detailResolved && !order) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem', background: 'var(--clr-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={28} />
      </div>
    );
  }

  if (detailResolved && (!order || (currentUser?.role !== 'admin' && order.user_id !== currentUser?.id))) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem', background: 'var(--clr-bg)' }}>
        <Card style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px', color: 'var(--clr-text)' }}>Order not found</div>
          <div style={{ fontSize: '14px', color: 'var(--clr-text-2)', marginBottom: '14px' }}>This order may no longer exist or you may not have access to view it.</div>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const canCancel = currentUser?.role !== 'admin' && !['COMPLETED', 'CANCELLED'].includes(order.status);
  const handleCancel = async () => {
    const response = await cancelOrder(order.order_id);
    if (response.success) {
      setFallbackOrder(response.order);
      toast.success(response.message || 'Order cancelled.');
    } else {
      toast.error(response.error || 'We could not cancel this order right now.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--clr-bg)' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem 3rem' }}>
        <button onClick={() => navigate('/dashboard')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', color: 'var(--clr-text-2)', fontSize: '13px' }}>
          <ArrowLeft size={15} /> Back to dashboard
        </button>

        <div className="order-details-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)', gap: '16px' }}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                  Order details
                </div>
                <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '6px' }}>{order.order_id}</div>
                <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>
                  {order.type === 'buy' ? 'Buy order' : 'Sell order'} created on {formatDate(order.created_at)}
                </div>
              </div>
              <Badge status={order.status}>{order.status}</Badge>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '1.25rem' }}>
              <DetailBox label="Asset" value={order.crypto_type?.toUpperCase()} />
              <DetailBox label="Crypto amount" value={String(order.crypto_amount)} />
              <DetailBox label="Crypto value" value={`$${(order.usd_value || 0).toFixed(2)}`} />
              {order.type === 'buy' && <DetailBox label={`${order.network} fee`} value={`$${(order.fee_usd || 0).toFixed(2)}`} />}
              {order.type === 'buy' && <DetailBox label="Total payable" value={`$${(order.total_usd_value || order.usd_value || 0).toFixed(2)}`} accent />}
              <DetailBox label="Naira value" value={formatNGN(order.naira_value)} accent />
              <DetailBox label="Rate used" value={formatNGN(order.rate_used)} />
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              <InfoRow label="Wallet / Deposit address" value={truncateAddress(order.wallet_address, 12, 8)} action={() => copy(order.wallet_address)} />
              <InfoRow label="Network" value={order.network} />
              {order.payer_name && <InfoRow label="Approved payment name" value={order.payer_name} />}
              {order.type === 'buy' && order.crypto_type === 'btc' && <InfoRow label="Wallet expires" value={order.wallet_address_expires ? 'Yes' : 'No'} />}
              {order.type === 'buy' && order.crypto_type === 'btc' && order.wallet_address_expires && order.wallet_expiry_contact_phone && <InfoRow label="Expiry contact phone" value={order.wallet_expiry_contact_phone} />}
              {order.type === 'buy' && order.crypto_type === 'btc' && order.wallet_address_expires && order.wallet_expiry_minutes ? <InfoRow label="Expiry window" value={`${order.wallet_expiry_minutes} minutes`} /> : null}
              {order.bank_name && <InfoRow label="Bank" value={order.bank_name} />}
              {order.account_name && <InfoRow label="Account name" value={order.account_name} />}
              {order.account_number && <InfoRow label="Account number" value={order.account_number} />}
              <InfoRow label="Proof file" value={order.proof_image || 'Not uploaded yet'} />
              {order.rate_locked_until && <InfoRow label="Rate lock until" value={formatDate(order.rate_locked_until)} />}
            </div>
          </Card>

          <div style={{ display: 'grid', gap: '16px' }}>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <Clock3 size={18} color="var(--clr-accent)" />
                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--clr-text)' }}>Order timeline</div>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                {order.timeline?.map((step) => (
                  <div key={step.id} style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: '10px' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: step.done ? 'var(--clr-accent-dim)' : 'var(--clr-surface-2)', color: step.done ? 'var(--clr-accent)' : 'var(--clr-text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px' }}>
                      {step.done ? <CheckCircle2 size={14} /> : <Clock3 size={14} />}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--clr-text)' }}>{step.label}</div>
                      <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.65 }}>{step.description}</div>
                      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginTop: '4px' }}>{step.at ? formatDate(step.at) : 'Pending'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <FileCheck size={18} color="var(--clr-accent)" />
                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--clr-text)' }}>Quick actions</div>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Button variant="ghost" onClick={() => copy(order.order_id)}>Copy order ID</Button>
                <Button variant="ghost" onClick={() => copy(order.wallet_address)}>Copy address</Button>
                {canCancel && (
                  <Button variant="ghost" onClick={handleCancel}>
                    Cancel order
                  </Button>
                )}
                <Button variant="primary" onClick={() => navigate(order.type === 'buy' ? '/buy' : '/sell')}>
                  Create another {order.type === 'buy' ? 'buy' : 'sell'} order
                </Button>
              </div>
            </Card>
          </div>
        </div>

        <style>{`
          @media (max-width: 860px) {
            .order-details-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

function DetailBox({ label, value, accent = false }) {
  return (
    <div style={{ padding: '14px', borderRadius: '14px', background: 'var(--clr-surface-2)' }}>
      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 800, color: accent ? 'var(--clr-accent)' : 'var(--clr-text)' }}>{value}</div>
    </div>
  );
}

function InfoRow({ label, value, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingBottom: '10px', borderBottom: '1px solid var(--clr-border)' }}>
      <div>
        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text)' }}>{value}</div>
      </div>
      {action && (
        <button onClick={action} style={{ color: 'var(--clr-text-2)', padding: '4px' }}>
          <Copy size={14} />
        </button>
      )}
    </div>
  );
}
